// V5-PATCH: additive, no behavior change.
// Two compact USD macro cards mounted under country rate grids.
// Reuses the existing useGoldRates feed — no new API keys, no new scripts.
import { useGoldRates } from "@/lib/nonblocking-rates";
import type { Country } from "@/lib/gold-data";

function fmtUsd(n: number, frac = 2) {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: frac, maximumFractionDigits: frac })}`;
}

function Card({ label, headline, sub, source }: { label: string; headline: string; sub?: string; source: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-2 text-2xl md:text-3xl font-black text-foreground">{headline}</div>
      {sub && <div className="mt-1 text-sm text-muted-foreground">{sub}</div>}
      <div className="mt-3 text-[11px] text-muted-foreground">{source}</div>
    </div>
  );
}

export function MacroCards({ country }: { country: Country }) {
  const { data } = useGoldRates();
  const spotPerOz = data.spotUsdPerOz;
  const spotPerG = spotPerOz / 31.1035;
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const fxLabel = country === "pakistan" ? "USD → PKR (Interbank)" : "USD → INR (Interbank)";
  const currency = country === "pakistan" ? "PKR" : "INR";

  return (
    <section
      className="mx-auto max-w-6xl px-4 mt-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
      style={{ minHeight: 160 }}
      aria-label="USD macro indicators"
    >
      <Card
        label="Global Spot Gold (USD)"
        headline={`${fmtUsd(spotPerOz)} / oz`}
        sub={`${fmtUsd(spotPerG)} per gram · 24K reference`}
        source="Source: international spot · refreshed every 30 min"
      />
      <Card
        label={fxLabel}
        headline={`1 USD = ${fx.toFixed(2)} ${currency}`}
        sub="Open-market interbank reference"
        source="Source: aggregated FX feed · refreshed every 30 min"
      />
    </section>
  );
}

export default MacroCards;